import { useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import ComboBox from '@/components/ui/ComboBox'
import MultiComboBox from '@/components/ui/MultiComboBox'
import Title from './Title'
import InputTile from './InputTile'
import { helpers as brandingHelpers } from '@/features/branding'
import { FileUpload } from '@/features/files'
import { allLocalesAsOptions } from '@/features/translations'
import { FormData } from '../hooks/useBranding'

export default function Location() {
  const { t } = useLingui()

  return (
    <InputTile>
      <Title
        title={t`Hotel Information`}
        description={t`Setting to different general informations of the Hotel.`}
      />
      <Form.Field
        name="currency"
        size="small"
        label={t`Currency`}
        description={t`Choose the Currency that is shown in the GuestApp.`}
        rules={{
          required: t`This field is required`
        }}
      >
        {({ renderProps: props }) => (
          <ComboBox
            options={brandingHelpers.currencies}
            placeholder={t`Choose Currency`}
            {...props}
          />
        )}
      </Form.Field>
      <Form.Field
        name="timezone"
        size="small"
        label={t`Timezone`}
        description={t`The timezone of the Hotel, we show the calendar/orders/events etc. in this Timezone.`}
        rules={{
          required: t`This field is required`
        }}
      >
        {({ renderProps: props }) => (
          <ComboBox
            options={brandingHelpers.timezones}
            placeholder={t`Choose a Timezone`}
            {...props}
          />
        )}
      </Form.Field>
      <Form.Field
        name="defaultLocale"
        size="small"
        label={t`Default Language`}
        description={t`The default language that is used by your App.`}
        rules={{
          required: t`This field is required`
        }}
      >
        {({ renderProps: props }) => (
          <ComboBox
            options={allLocalesAsOptions}
            placeholder={t`Choose a Language`}
            {...props}
          />
        )}
      </Form.Field>
      <Form.Field
        name="supportedLocales"
        size="small"
        label={t`Supported Languages`}
        description={t`Which languages are supported next to your default language by your App, these are shown when you edit Objects that can be translated.`}
      >
        {({ renderProps: { value, ...props } }) => (
          <Form.Data<FormData>>
            {({ defaultLocale }) => (
              <MultiComboBox
                options={allLocalesAsOptions.filter(
                  loc => loc.value !== defaultLocale
                )}
                placeholder={t`Choose Languages`}
                value={value ?? []}
                {...props}
              />
            )}
          </Form.Data>
        )}
      </Form.Field>
      <Form.Field
        name="imprintUrl"
        label={t`Legal Notice`}
        description={t`The legal notice url of the Hotel, we can then show this in the Guest App to the users`}
        rules={{
          maxLength: {
            value: 255,
            message: t`Can't be longer than 255 characters.`
          },
          validate: {
            isUrl: Form.Validations.urlValidation(t`Has to be a valid URL.`)
          }
        }}
      >
        {({ renderProps: { onChange, ...props } }) => (
          <Input
            onChange={event => onChange(event.target.value)}
            type="url"
            placeholder={t`Legal Notice URL`}
            {...props}
          />
        )}
      </Form.Field>
      <Form.Field
        name="phoneNumber"
        label={t`Reception phone number`}
        description={t`You can show a phone number in the settings of the GuestApp, so that guests know where they can reach the reception.`}
        rules={{
          maxLength: {
            value: 255,
            message: t`Can't be longer than 255 characters.`
          }
        }}
      >
        {({ renderProps: { onChange, ...props } }) => (
          <Input
            onChange={event => onChange(event.target.value)}
            type="tel"
            placeholder="+43 1 234567"
            {...props}
          />
        )}
      </Form.Field>
      <Form.Field
        name="map"
        label={t`Map of the Hotel`}
        description={t`Here you can add a map of your hotel, it will be shown to the guest directly on their personal page.`}
      >
        {({ renderProps: { onChange, ...props } }) => (
          <FileUpload
            className="h-20 w-48"
            objectFit="contain"
            onChange={onChange}
            onRemove={() => onChange(null)}
            placeholder={t`Choose the map for your hotel.`}
            {...props}
          />
        )}
      </Form.Field>
    </InputTile>
  )
}
